import type { SmartWorkspaceDTO } from "@eu.mdotm.api/smart-workspaces";
import { type Atom, atom, selectAtom } from "@mdotm/shadcn/legacy/reactivity";
import { instanceEqualsOrFastDeepEqual } from "@mdotm/shadcn/legacy/utils";
import type { ColumnState } from "ag-grid-community";
import { workspaceAdapter } from "../ctx/adapter";
import type { ParameterizedConceptDTOWithMandatoryUuid, Workspace, WorkspaceChartCtx } from "../ctx/context";
import { selectConceptColumns, selectPagination } from "./selectors";
import { type WorkspaceState, privateConcepts, resolveConcepts, sharedConcepts } from "./state";

/**
 * The render subscriptions, one per thing a view actually watches.
 *
 * Every one is an {@link Atom} and never an `RwAtom`: a writable slice handed to a component is how the
 * eighteen bypassing writes would come back with nicer syntax, so a view is given the read half only and
 * every write is a `dispatch`. `deriveNestedAtom` is not used here for exactly that reason — it returns a
 * read-*write* atom.
 *
 * **Why this is a layer and not one subscription.** The store is one value, so a component reading it whole
 * would re-render on every action — including `setSearch`, while the AG-Grid table subscribes to columns,
 * charts and concepts. The bail-out is structural rather than a deep compare per slice: the four top-level
 * slices are plain property reads compared by identity, and the reducer builds a new `content` object only
 * when content changed (`withContent`) and a new `ui` only when ui changed (`withUi`). So `setSearch`
 * provably cannot notify `columns$` — the same property the reducer's property tests assert by identity.
 *
 * Everything narrower is chained off those four, so it is not recomputed unless its half of the state moved.
 * The slices whose value is *derived* rather than read carry an equality function as well, because they mint
 * a new value on every recompute and identity alone would notify.
 */
export type WorkspaceSlices = {
	/** The upstream document, for the fields the client does not model: uuid, name, taxonomy, acl, status. */
	document$: Atom<SmartWorkspaceDTO | undefined>;
	/** The whole pool, private then shared, deduplicated — what `allParametrizedConcepts$` derived. */
	concepts$: Atom<ParameterizedConceptDTOWithMandatoryUuid[]>;
	privateConcepts$: Atom<ParameterizedConceptDTOWithMandatoryUuid[]>;
	sharedConcepts$: Atom<ParameterizedConceptDTOWithMandatoryUuid[]>;
	view$: Atom<Workspace["view"]>;
	filters$: Atom<Workspace["filters"]>;
	columns$: Atom<Workspace["columns"]>;
	/** The columns whose `colId` is a concept uuid, i.e. the ones the workspace owns. */
	conceptColumns$: Atom<ColumnState[] | undefined>;
	charts$: Atom<WorkspaceChartCtx[]>;
	sort$: Atom<Workspace["sort"]>;
	search$: Atom<Workspace["search"]>;
	selection$: Atom<Workspace["selection"]>;
	pagination$: Atom<Workspace["pagination"]>;
	/**
	 * The workspace as it would be persisted — what `workspaceAdapter.useSmartWorkspace` returned off the ten
	 * atoms, subscribed to narrowly instead. The table still reads `grid.projection` and `viewMode` off it.
	 */
	dto$: Atom<SmartWorkspaceDTO>;
	/**
	 * The state a data request is built from, for the surfaces that call `buildDataRequest`.
	 *
	 * A selector takes the whole state, and a component that subscribed to the whole state would re-render on
	 * every action — so this is the state with the fields a request provably does not read zeroed, which is
	 * the same trick (and the same `offTheWire` triple) `wireState$` plays one field over. The live half is
	 * the search box, because the predicate does read it; the zeroed half is the selection, the fetched row
	 * count, and the page, so that a row click, a response and a paginator click cannot notify this. The page
	 * is not missing information: `offset` reaches a request through the caller's tail, from the surface that
	 * actually paginates.
	 *
	 * Read-only, like every other slice: `buildDataRequest` is a function of state, not a way to hold one.
	 */
	requestState$: Atom<WorkspaceState>;
};

/**
 * The ephemeral slice the serializer is handed.
 *
 * `contextToSmartWorkspaceConverter` reads `pagination.size` — which is `content.pageSize` — and nothing else
 * out of `ui`; `ctx/adapter.test.ts` pins `page` and `total` coming back as `1` / `0`. So the document is a
 * function of the other three slices, and saying so here is what keeps re-serializing it off the per-search
 * and per-row-click path.
 */
const offTheWire: WorkspaceState["ui"] = { search: "", selection: [], page: 1, resultTotal: 0 };

export function createWorkspaceSlices(state$: Atom<WorkspaceState>): WorkspaceSlices {
	const meta$ = selectAtom(state$, (state) => state.meta);
	const pool$ = selectAtom(state$, (state) => state.concepts);
	const content$ = selectAtom(state$, (state) => state.content);
	const ui$ = selectAtom(state$, (state) => state.ui);
	const search$ = selectAtom(ui$, (ui) => ui.search);

	const wireState$ = atom(
		(get): WorkspaceState => ({ meta: get(meta$), concepts: get(pool$), content: get(content$), ui: offTheWire }),
	);

	const requestState$ = atom(
		(get): WorkspaceState => ({
			meta: get(meta$),
			concepts: get(pool$),
			content: get(content$),
			ui: { ...offTheWire, search: get(search$) },
		}),
	);

	return {
		document$: selectAtom(meta$, (meta) => meta.document),
		concepts$: selectAtom(pool$, resolveConcepts, instanceEqualsOrFastDeepEqual),
		privateConcepts$: selectAtom(pool$, privateConcepts, instanceEqualsOrFastDeepEqual),
		sharedConcepts$: selectAtom(pool$, sharedConcepts, instanceEqualsOrFastDeepEqual),
		view$: selectAtom(content$, (content) => content.view),
		filters$: selectAtom(content$, (content) => content.filters),
		columns$: selectAtom(content$, (content) => content.columns),
		conceptColumns$: selectAtom(state$, selectConceptColumns, instanceEqualsOrFastDeepEqual),
		charts$: selectAtom(content$, (content) => content.charts),
		sort$: selectAtom(content$, (content) => content.sort),
		search$,
		selection$: selectAtom(ui$, (ui) => ui.selection),
		// Three numbers reassembled from both halves, so it is the one slice that has to be compared by value.
		pagination$: selectAtom(state$, selectPagination, instanceEqualsOrFastDeepEqual),
		dto$: selectAtom(wireState$, workspaceAdapter.fromState, instanceEqualsOrFastDeepEqual),
		requestState$,
	};
}
